// apps/web/src/features/commercial-proposals/components/CommercialProposalDrawer.tsx
//
// #1198 detail drawer over `/kp/:id`. Loads the owner/Admin detail, shows
// the content + backend `calculation` read-only, the lifetime HTML view
// count, an internal preview, and the draft/published action set. A DRAFT
// can be edited in place (PATCH); a PUBLISHED proposal is read-only and must
// be revoked to draft before editing.
//
// @aiInvariant the internal preview renders from the ALREADY-LOADED detail —
//   it never calls a public endpoint and never increments public-view
//   analytics. Edit mode mounts the shared editor body inside this drawer's
//   own `EditorDrawer`, so the unsaved-changes guard stays a single instance.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { CommercialProposalStatus, type CommercialProposalDetailResponse } from '@magimp/shared';

import { EditorDrawer, IconButton, useEditorGuard, useNarrow } from '../../../ui/index.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import type { AppLocale } from '../../../i18n/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { useCommercialProposalDetail, usePatchCommercialProposal } from '../queries.js';
import { contentToForm, formToContent } from '../formModel.js';
import {
  useCommercialProposalForm,
  type UseCommercialProposalFormResult,
} from '../useCommercialProposalForm.js';
import { CommercialProposalDrawerBody } from './CommercialProposalDrawerBody.js';
import { CommercialProposalDrawerActions } from './CommercialProposalDrawerActions.js';
import { CommercialProposalEditorBody } from './CommercialProposalEditorBody.js';

export function CommercialProposalDrawer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const close = () => navigate('/kp');
  if (!id) return null;
  return <CommercialProposalDrawerInner proposalId={id} onClose={close} />;
}

function CommercialProposalDrawerInner({
  proposalId,
  onClose,
}: {
  proposalId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('commercialProposals');
  const query = useCommercialProposalDetail(proposalId);
  const narrow = useNarrow();
  const [editing, setEditing] = useState(false);
  const patch = usePatchCommercialProposal(proposalId);
  const permissions = useAuthStore((s) => s.permissions);
  const canManage = hasPermission(permissions, 'commercialProposals.manage');

  // In view mode the drawer is never dirty; edit mode delegates dirtiness to
  // the inner editor via the render-prop below. We hold a dirty mirror so the
  // EditorDrawer guard sees the editor's state.
  const [dirty, setDirty] = useState(false);
  const busy = patch.isPending;

  return (
    <EditorDrawer
      dirty={editing && dirty}
      busy={busy}
      onConfirmClose={onClose}
      width={680}
      narrow={narrow}
    >
      <div
        data-testid="commercial-proposals-drawer"
        style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}
      >
        {query.isLoading ? (
          <DrawerShell title={t('title')} onClose={onClose}>
            <p
              data-testid="commercial-proposals-drawer-loading"
              style={{ color: 'var(--fg-3)', fontSize: 13 }}
            >
              {t('drawer.loading')}
            </p>
          </DrawerShell>
        ) : query.isError || !query.data ? (
          <DrawerShell title={t('title')} onClose={onClose}>
            <p
              data-testid="commercial-proposals-drawer-error"
              role="alert"
              style={{ color: 'var(--status-error-fg)', fontSize: 13 }}
            >
              {t('drawer.error')}
            </p>
          </DrawerShell>
        ) : editing &&
          canManage &&
          query.data.proposal.status === CommercialProposalStatus.DRAFT ? (
          <DrawerEditMode
            detail={query.data}
            onCancel={() => {
              setEditing(false);
              setDirty(false);
            }}
            onDirtyChange={setDirty}
            patch={patch}
          />
        ) : (
          <DrawerViewMode
            detail={query.data}
            canManage={canManage}
            onClose={onClose}
            onEdit={() => setEditing(true)}
          />
        )}
      </div>
    </EditorDrawer>
  );
}

function DrawerShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <>
      <Header title={title} onClose={onClose} />
      <div style={{ flex: 1, padding: '16px 20px', overflow: 'auto', minHeight: 0 }}>
        {children}
      </div>
    </>
  );
}

function Header({ title, onClose }: { title: string; onClose: () => void }) {
  const { t } = useTranslation('commercialProposals');
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid var(--border-1)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--fg-1)' }}>{title}</h2>
      <IconButton
        icon="x"
        label={t('editor.cancel')}
        onClick={onClose}
        data-testid="commercial-proposals-drawer-close"
      />
    </div>
  );
}

function DrawerViewMode({
  detail,
  canManage,
  onClose,
  onEdit,
}: {
  detail: CommercialProposalDetailResponse;
  canManage: boolean;
  onClose: () => void;
  onEdit: () => void;
}) {
  const { i18n } = useTranslation('commercialProposals');
  const [previewOpen, setPreviewOpen] = useState(false);
  const language = i18n.language as AppLocale;
  return (
    <>
      <Header title={detail.proposal.publicCode} onClose={onClose} />
      <div style={{ flex: 1, padding: '16px 20px', overflow: 'auto', minHeight: 0 }}>
        <CommercialProposalDrawerBody
          detail={detail}
          language={language}
          previewOpen={previewOpen}
        />
      </div>
      <CommercialProposalDrawerActions
        detail={detail}
        canManage={canManage}
        onTogglePreview={() => setPreviewOpen((v) => !v)}
        onEdit={onEdit}
      />
    </>
  );
}

function DrawerEditMode({
  detail,
  onCancel,
  onDirtyChange,
  patch,
}: {
  detail: CommercialProposalDetailResponse;
  onCancel: () => void;
  onDirtyChange: (dirty: boolean) => void;
  patch: ReturnType<typeof usePatchCommercialProposal>;
}) {
  const formApi = useCommercialProposalForm(
    contentToForm(detail.proposal.content),
    detail.proposal.content,
  );
  // Mirror the inner editor's dirty flag up to the EditorDrawer guard.
  useEffect(() => onDirtyChange(formApi.dirty), [formApi.dirty, onDirtyChange]);
  return (
    <DrawerEditModeInner detail={detail} formApi={formApi} onCancel={onCancel} patch={patch} />
  );
}

function DrawerEditModeInner({
  detail,
  formApi,
  onCancel,
  patch,
}: {
  detail: CommercialProposalDetailResponse;
  formApi: UseCommercialProposalFormResult;
  onCancel: () => void;
  patch: ReturnType<typeof usePatchCommercialProposal>;
}) {
  const { t, i18n } = useTranslation('commercialProposals');
  const { bypassNext } = useEditorGuard();
  const showToast = useToastStore((s) => s.show);
  const { form, setField, preview, valid } = formApi;

  const submit = () =>
    patch.mutate(
      { content: formToContent(form, detail.proposal.content) },
      {
        onSuccess: () => {
          bypassNext();
          onCancel();
        },
        onError: () => showToast({ tone: 'error', text: t('editor.saveError') }),
      },
    );

  return (
    <CommercialProposalEditorBody
      title={t('editor.editTitle')}
      form={form}
      setField={setField}
      preview={preview}
      valid={valid}
      language={i18n.language as AppLocale}
      submitting={patch.isPending}
      submitLabel={patch.isPending ? t('editor.saving') : t('editor.save')}
      onClose={onCancel}
      onSubmit={submit}
      proposalId={detail.proposal.id}
      savedPhotoObjectKey={detail.proposal.content.photoObjectKey ?? ''}
    />
  );
}
